import { Industry } from '@clipr/lib';

/**
 * The one source of the wording that differs between a Public Safety agency and every other
 * industry. The agency consoles (sponsor dashboard, Agency Admin) and the shared sample uploader
 * read their visible copy through this map; routes, types and stored values stay unchanged.
 *
 * Lower-case keys are mid-sentence forms; capitalized keys start a heading or sentence.
 */
export type AgencyTerminology = {
  org: string;
  Org: string;
  orgs: string;
  officer: string;
  Officer: string;
  officers: string;
  Officers: string;
  supervisor: string;
  Supervisor: string;
  /** 'Patrol narrative' keeps its capital P as a product name; the generic form does not. */
  narrative: string;
  Narrative: string;
  swornOfficers: string;
  SwornOfficers: string;
  orgWide: string;
  OrgWide: string;
  department: string;
  rosterSetup: string;
  Roster: string;
  rosterRecord: string;
  rosterSection: string;
  /**
   * The whole no-sample sentence rather than a substituted term: "libraries default to our
   * default report format" is what pure substitution of "Patrol 1st Draft" produces.
   */
  noSampleHint: string;
};

const PUBLIC_SAFETY_TERMS: AgencyTerminology = {
  org: 'agency',
  Org: 'Agency',
  orgs: 'agencies',
  officer: 'officer',
  Officer: 'Officer',
  officers: 'officers',
  Officers: 'Officers',
  supervisor: 'supervisor',
  Supervisor: 'Supervisor',
  narrative: 'Patrol narrative',
  Narrative: 'Patrol narrative',
  swornOfficers: 'sworn officers',
  SwornOfficers: 'Sworn officers',
  orgWide: 'agency-wide',
  OrgWide: 'Agency-wide',
  department: 'department',
  rosterSetup: 'roster setup',
  Roster: 'Roster',
  rosterRecord: 'roster record',
  rosterSection: 'roster',
  noSampleHint:
    'If no sample is uploaded, libraries default to our Patrol 1st Draft. '
    + 'You can upload one later to retrain new libraries.',
};

const GENERIC_TERMS: AgencyTerminology = {
  org: 'organization',
  Org: 'Organization',
  orgs: 'organizations',
  officer: 'employee',
  Officer: 'Employee',
  officers: 'employees',
  Officers: 'Employees',
  supervisor: 'manager',
  Supervisor: 'Manager',
  narrative: 'report narrative',
  Narrative: 'Report narrative',
  swornOfficers: 'employees',
  SwornOfficers: 'Employees',
  orgWide: 'organization-wide',
  OrgWide: 'Organization-wide',
  department: 'organization',
  rosterSetup: 'employee setup',
  Roster: 'Registered employees',
  rosterRecord: 'registered employee',
  rosterSection: 'registered',
  noSampleHint:
    'If no sample is uploaded, libraries use the default report format. '
    + 'You can upload one later to retrain new libraries.',
};

/**
 * An absent industry means Public Safety: every organization predating the industry field is a
 * law-enforcement agency, and its copy must not change out from under it.
 */
export const agencyTerminology = (industry?: Industry | null): AgencyTerminology =>
  (!industry || industry === Industry.PublicSafety ? PUBLIC_SAFETY_TERMS : GENERIC_TERMS);
